import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from './axios';
// types
import {
  TOrganisationData,
  TOrganisationResponse,
  TOrganisationSaveRequest,
  TOrganisationUploadRequest,
  TOrganisationUploadResponse,
} from '../types/companySettings';

const organisationKeys = {
  all: ['organisation'] as const,
  detail: () => [...organisationKeys.all, 'detail'] as const,
};

// GET /organisation/fetch - Fetch organisation data
export const useGetOrganisation = () => {
  return useQuery({
    queryKey: organisationKeys.detail(),
    queryFn: async () => {
      const { data } = await axiosInstance.get<TOrganisationData>(
        '/organisation/fetch'
      );
      return data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1, // Only retry once to avoid excessive re-renders on failure
  });
};

// POST /organisation/save - Save organisation data
export const useSaveOrganisation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: TOrganisationSaveRequest) => {
      const { data } = await axiosInstance.post<TOrganisationResponse>(
        '/organisation/save',
        request
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate organisation query to refetch updated data
      queryClient.invalidateQueries({ queryKey: organisationKeys.all });
    },
  });
};

// POST /organisation/upload?fileName={key} - Upload file
export const useUploadOrganisationFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, fileName }: TOrganisationUploadRequest) => {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axiosInstance.post<TOrganisationUploadResponse>(
        `/organisation/upload?fileName=${fileName}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate organisation query to refetch with new file paths
      queryClient.invalidateQueries({ queryKey: organisationKeys.all });
    },
  });
};

export const organisationServices = {
  organisationKeys,
  useGetOrganisation,
  useSaveOrganisation,
  useUploadOrganisationFile,
};
